import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, XCircle, Clock, Gavel } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending:  "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};

/**
 * Approvals inbox: pending items requiring maker-checker sign-off.
 * Admin/manager also see + edit approval rules (threshold per resource).
 */
export default function Approvals() {
  const [rows, setRows] = useState([]);
  const [rules, setRules] = useState([]);
  const [status, setStatus] = useState("pending");
  const [ruleForm, setRuleForm] = useState({ resource: "expense", threshold: 10000, approver_role: "manager", active: true });
  const [busy, setBusy] = useState(false);
  const [reasonMap, setReasonMap] = useState({});

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/approvals", { params: { status } }),
        api.get("/approval-rules"),
      ]);
      setRows(a.data);
      setRules(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const act = async (aid, action) => {
    setBusy(true);
    try {
      await api.post(`/approvals/${aid}/${action}`, { reason: reasonMap[aid] || "" });
      toast.success(`Approval ${action}d`);
      setReasonMap((m) => { const c = { ...m }; delete c[aid]; return c; });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveRule = async () => {
    setBusy(true);
    try {
      await api.post("/approval-rules", {
        ...ruleForm,
        threshold: Number(ruleForm.threshold) || 0,
      });
      toast.success("Rule updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const rulesByResource = useMemo(() => {
    const map = {};
    rules.filter((r) => r.active).forEach((r) => { map[r.resource] = r; });
    return map;
  }, [rules]);

  return (
    <div data-testid="approvals-page">
      <PageHeader
        title="Approvals"
        subtitle="Maker-checker workflow for expenses, purchases, and vendor POs above threshold."
        module="Governance"
        actions={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="approvals-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <div className="p-8 space-y-8">
        {/* Approval rules */}
        <section className="bg-white border border-slate-200 rounded-sm">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[15px] font-semibold font-display">Approval Thresholds</div>
              <div className="text-[11.5px] text-slate-500">Rules that decide when a maker-checker workflow is required.</div>
            </div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {["expense", "purchase", "vendor_po"].map((r) => (
              <div key={r} className="border border-slate-200 rounded-sm p-3">
                <div className="label-caps text-slate-500">{r.replace("_", " ")}</div>
                <div className="text-xl font-semibold font-mono-num text-slate-800 mt-1">
                  ≥ {fmtMoney(rulesByResource[r]?.threshold ?? 0)}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Approver: <strong>{rulesByResource[r]?.approver_role ?? "—"}</strong>
                </div>
              </div>
            ))}
          </div>
          <div className="p-5 border-t border-slate-100 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="label-caps mb-1 block">Resource</Label>
              <Select value={ruleForm.resource} onValueChange={(v) => setRuleForm({ ...ruleForm, resource: v })}>
                <SelectTrigger data-testid="rule-resource"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="purchase">Purchase</SelectItem>
                  <SelectItem value="vendor_po">Vendor PO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps mb-1 block">Threshold (₹)</Label>
              <Input
                type="number" step="1"
                data-testid="rule-threshold"
                value={ruleForm.threshold}
                onChange={(e) => setRuleForm({ ...ruleForm, threshold: e.target.value })}
              />
            </div>
            <div>
              <Label className="label-caps mb-1 block">Approver role</Label>
              <Select value={ruleForm.approver_role} onValueChange={(v) => setRuleForm({ ...ruleForm, approver_role: v })}>
                <SelectTrigger data-testid="rule-approver"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={saveRule} disabled={busy} data-testid="save-rule" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">
                {busy ? "Saving…" : "Update rule"}
              </Button>
            </div>
          </div>
        </section>

        {/* Approvals inbox */}
        <section>
          {rows.length === 0 ? (
            <EmptyState title={`No ${status} approvals`} description="When high-value expenses or POs are raised, they'll appear here for sign-off." />
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm" data-testid="approvals-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="label-caps">Resource</TableHead>
                    <TableHead className="label-caps">Amount</TableHead>
                    <TableHead className="label-caps">Requested by</TableHead>
                    <TableHead className="label-caps">Created</TableHead>
                    <TableHead className="label-caps">Status</TableHead>
                    <TableHead className="w-1/2" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((a) => (
                    <TableRow key={a.id} data-testid={`approval-row-${a.id}`}>
                      <TableCell>
                        <span className="text-[10px] uppercase tracking-widest font-semibold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm">
                          {a.resource.replace("_", " ")}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono-num font-semibold text-red-700">{fmtMoney(a.amount)}</TableCell>
                      <TableCell className="text-slate-700">{a.requested_by_email}</TableCell>
                      <TableCell className="text-slate-500 text-[12px]">{new Date(a.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm border ${STATUS_COLORS[a.status]}`}>
                          {a.status === "pending" && <Clock size={11} />}
                          {a.status === "approved" && <CheckCircle size={11} weight="fill" />}
                          {a.status === "rejected" && <XCircle size={11} weight="fill" />}
                          {a.status}
                        </span>
                        {a.reason && <div className="text-[11px] text-slate-500 mt-1 italic">&ldquo;{a.reason}&rdquo;</div>}
                      </TableCell>
                      <TableCell>
                        {a.status === "pending" ? (
                          <div className="flex gap-2 items-center">
                            <Input
                              value={reasonMap[a.id] || ""}
                              onChange={(e) => setReasonMap((m) => ({ ...m, [a.id]: e.target.value }))}
                              placeholder="Reason (optional)"
                              data-testid={`approval-reason-${a.id}`}
                              className="h-8 text-xs flex-1"
                            />
                            <Button
                              size="sm"
                              onClick={() => act(a.id, "approve")}
                              disabled={busy}
                              data-testid={`approve-${a.id}`}
                              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => act(a.id, "reject")}
                              disabled={busy}
                              data-testid={`reject-${a.id}`}
                              className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 h-8 text-xs"
                            >
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <div className="text-[11px] text-slate-400">
                            {a.acted_at && `by ${a.approved_by_email} · ${new Date(a.acted_at).toLocaleString()}`}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
